import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { OverlayPortalContext } from "../overlay-portal-context";
import {
  hasOpenOverlayRoot,
  isPortalledOverlayTarget,
  isTopOverlayRoot,
  registerOverlayRoot,
  unregisterOverlayRoot,
} from "../overlay-isolation";
import "../overlay-foundations.css";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const getFocusableElements = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getAttribute("aria-disabled") !== "true",
  );

const hasHigherDialog = () =>
  Boolean(document.querySelector("[data-ui-modal-root]"));

const hasOpenNestedEscapeLayer = (root) =>
  Boolean(
    root.querySelector(
      '[data-ui-overlay-dismiss-layer]:not([data-state="closed"])',
    ),
  );

const Drawer = ({
  isOpen,
  onClose,
  children,
  side = "start",
  title,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  initialFocusRef,
  initialFocusSelector,
  closeOnBackdrop = true,
  closeOnEscape = true,
  keepMounted = false,
  className = "",
  backdropClassName = "",
  backdropProps = {},
  ...surfaceProps
}) => {
  const generatedTitleId = useId();
  const rootRef = useRef(null);
  const drawerRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [overlayPortalContainer, setOverlayPortalContainer] = useState(null);
  onCloseRef.current = onClose;

  const attachRoot = useCallback((node) => {
    rootRef.current = node;
    if (node) setOverlayPortalContainer(node);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const root = rootRef.current;
    const drawer = drawerRef.current;
    if (!root || !drawer) return undefined;

    restoreFocusRef.current = document.activeElement;
    registerOverlayRoot(root, restoreFocusRef.current);

    const requestedInitialFocus = initialFocusRef?.current;
    const focusTarget =
      (requestedInitialFocus && drawer.contains(requestedInitialFocus)
        ? requestedInitialFocus
        : null) ||
      (initialFocusSelector
        ? drawer.querySelector(initialFocusSelector)
        : null) ||
      getFocusableElements(drawer)[0] ||
      drawer;
    focusTarget.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (!isTopOverlayRoot(root) || hasHigherDialog()) return;

      if (isPortalledOverlayTarget(event.target, drawer)) return;

      if (event.key === "Escape" && hasOpenNestedEscapeLayer(root)) return;

      if (event.key === "Escape" && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusableElements(drawer);
      if (focusable.length === 0) {
        event.preventDefault();
        drawer.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      const activeElement = document.activeElement;

      if (!drawer.contains(activeElement)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      const restoreTarget = unregisterOverlayRoot(root);
      if (restoreTarget?.isConnected) {
        queueMicrotask(() => {
          if (restoreTarget.isConnected && !hasOpenOverlayRoot()) {
            restoreTarget.focus({ preventScroll: true });
          }
        });
      }
    };
  }, [closeOnEscape, initialFocusRef, initialFocusSelector, isOpen]);

  if (!isOpen && !keepMounted) return null;

  const resolvedTitleId = title ? generatedTitleId : ariaLabelledBy;
  const sideClass = side === "end" ? "end-0" : "start-0";
  const closedSideClass =
    side === "end"
      ? "translate-x-full rtl:-translate-x-full"
      : "-translate-x-full rtl:translate-x-full";

  const drawer = (
    <div
      ref={attachRoot}
      data-ui-drawer-root=""
      data-state={isOpen ? "open" : "closed"}
      aria-hidden={isOpen ? undefined : "true"}
      className={cn("fixed inset-0 isolate", !isOpen && "pointer-events-none")}
      style={{ zIndex: "var(--layer-drawer)" }}
    >
      <div
        data-ui-drawer-backdrop=""
        data-state={isOpen ? "open" : "closed"}
        aria-hidden="true"
        className={cn(
          "ui-modal-backdrop ui-overlay-motion absolute inset-0 transition-opacity",
          isOpen ? "opacity-100" : "opacity-0",
          backdropClassName,
        )}
        onClick={(event) => {
          if (closeOnBackdrop && event.target === event.currentTarget)
            onClose();
        }}
        {...backdropProps}
      />
      <OverlayPortalContext.Provider value={overlayPortalContainer}>
        <div
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label={title || ariaLabelledBy ? undefined : ariaLabel}
          aria-labelledby={resolvedTitleId}
          tabIndex={-1}
          inert={isOpen ? undefined : true}
          data-ui-drawer-surface=""
          data-state={isOpen ? "open" : "closed"}
          data-side={side}
          className={cn(
            "ui-modal-surface ui-overlay-motion fixed inset-y-0 h-[100dvh] w-full max-w-[30rem] overflow-y-auto overscroll-contain border-e transition-transform outline-none",
            sideClass,
            isOpen ? "translate-x-0" : closedSideClass,
            className,
          )}
          {...surfaceProps}
        >
          {title ? (
            <h2 id={generatedTitleId} className="sr-only">
              {title}
            </h2>
          ) : null}
          {children}
        </div>
      </OverlayPortalContext.Provider>
    </div>
  );

  return createPortal(drawer, document.body);
};

export default Drawer;
export { Drawer };
